/**
 * Blog data access. Posts live in content/blog/*.md and are compiled by
 * scripts/build-blog.mjs into client/src/lib/generated/ (git-ignored).
 * Everything here is loaded lazily so no post body ever lands in the
 * main bundle.
 */
export interface BlogMeta {
  slug: string;
  title: string;
  description: string;
  date: string;
  updated?: string;
  readTime: string;
  image: string | null;
  imageAlt: string;
  topic: string;
  tags: string[];
  tools: string[];
  personas: string[];
  workflows: string[];
  /** Kept out of search results and the sitemap; the page still works for readers. */
  noindex?: boolean;
}

export interface BlogPostBody {
  meta: BlogMeta;
  htmlTop: string;
  htmlBottom: string;
  headings: { id: string; text: string }[];
  /** frontmatter tools that have no {{tool}} card in the body (shown as an end card) */
  ctaTools: string[];
  related: { slug: string; title: string; description: string; image: string | null }[];
  words: number;
}

export interface BlogLink {
  slug: string;
  title: string;
}

import guideLinks from "./generated/blog-guide-links.json";

const bodyLoaders = import.meta.glob<{ default: BlogPostBody }>("./generated/blog-posts/*.json");

let indexPromise: Promise<BlogMeta[]> | null = null;
export function loadBlogIndex(): Promise<BlogMeta[]> {
  indexPromise ??= import("./generated/blog-index.json").then(m => m.default as BlogMeta[]);
  return indexPromise;
}

export async function loadBlogPost(slug: string): Promise<BlogPostBody | null> {
  const load = bodyLoaders[`./generated/blog-posts/${slug}.json`];
  if (!load) return null;
  return (await load()).default;
}

/** Newest guides (capped at build time) for one tool / persona / workflow / topic. Static import so prerendered HTML includes them. */
export function guideLinksFor(kind: "tools" | "personas" | "workflows" | "topics", id: string): BlogLink[] {
  const links = (guideLinks as Record<string, Record<string, BlogLink[]>>)[kind]?.[id] ?? [];
  // A tool's own guide (the post named after it) comes first, ahead of posts that only mention the tool.
  return kind === "tools" ? [...links].sort((a, b) => Number(b.slug === id) - Number(a.slug === id)) : links;
}

export const BLOG_PAGE_SIZE = 12;
export const TOPIC_GENERAL = "guides";
export const SITE_BASE = "https://www.freedailypro.com";

export function formatBlogDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
}
